import type { Entity, Persisted } from '@tunasong/schemas'
import { useAlert, useFavorites } from '@tunasong/ui-lib'
import { useEffect, type RefObject } from 'react'
import { useFocusedRowId } from './use-focused-row-id.js'

interface UseSectionPinShortcutOptions {
  sectionRef: RefObject<HTMLElement | null>
  entities: Persisted<Entity>[]
}

const isEditorSurface = (el: Element | null): boolean => {
  if (!(el instanceof HTMLElement)) {
    return false
  }
  if (el.isContentEditable) {
    return true
  }
  return el.matches('textarea, input')
}

/**
 * Phase 2 dashboard: when a row inside `sectionRef` has DOM focus, pressing
 * `P`/`p` toggles its pin state. Modifier keys cancel the shortcut, as does
 * any focused editor surface (textarea, input, contentEditable). At cap, an
 * info toast is fired instead of silently no-oping.
 */
export const useSectionPinShortcut = (options: UseSectionPinShortcutOptions): void => {
  const { sectionRef, entities } = options
  const focusedId = useFocusedRowId(sectionRef)
  const { pin, unpin, isPinned, canPinMore, pinCap } = useFavorites()
  const { alert } = useAlert()

  useEffect(() => {
    const el = sectionRef.current
    if (!el) {
      return
    }
    const handler = (ev: KeyboardEvent) => {
      if (ev.key !== 'p' && ev.key !== 'P') {
        return
      }
      if (ev.metaKey || ev.ctrlKey || ev.altKey || ev.shiftKey) {
        return
      }
      if (isEditorSurface(document.activeElement)) {
        return
      }
      if (!focusedId) {
        return
      }
      const target = entities.find(e => e.id === focusedId)
      if (!target) {
        return
      }
      ev.preventDefault()
      if (isPinned(target.id)) {
        unpin(target.id)
        return
      }
      if (!canPinMore) {
        alert({
          severity: 'info',
          message: `You can pin up to ${pinCap} items. Unpin one to make room.`,
        })
        return
      }
      pin(target)
    }
    el.addEventListener('keydown', handler)
    return () => el.removeEventListener('keydown', handler)
  }, [sectionRef, entities, focusedId, pin, unpin, isPinned, canPinMore, pinCap, alert])
}
